import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardId } from '@blisspointmedia/bpm-types/dist/dashboarding';

const mirroredKey = (scope: string, company: string, dashboardId: DashboardId | null) => [
  scope,
  company,
  dashboardId
];

/**
 * A value DashboardBuilder publishes for AgentDrawer, which is mounted outside the builder's tree.
 * `null` while no builder has published one for this dashboard.
 */
export const useMirroredValue = <T>(
  scope: string,
  company: string,
  dashboardId: DashboardId | null
): T | null => {
  const queryClient = useQueryClient();
  const key = mirroredKey(scope, company, dashboardId);
  const { data = null } = useQuery<T | null>({
    queryKey: key,
    queryFn: () => queryClient.getQueryData<T | null>(key) ?? null,
    enabled: Boolean(company && dashboardId),
    staleTime: Infinity,
    gcTime: Infinity
  });
  return data;
};

export const usePublishMirroredValue = <T>(
  scope: string,
  company: string,
  dashboardId: DashboardId | null,
  value: T
): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (dashboardId) {
      queryClient.setQueryData(mirroredKey(scope, company, dashboardId), value);
    }
  }, [queryClient, scope, company, dashboardId, value]);

  // Cleared on unmount (or a switch to another dashboard), so a later visit can't ground the agent on
  // a value that no longer exists.
  useEffect(
    () => () => {
      if (dashboardId) {
        queryClient.setQueryData(mirroredKey(scope, company, dashboardId), null);
      }
    },
    [queryClient, scope, company, dashboardId]
  );
};
